import { createServerOnlyFn } from "@tanstack/react-start";
import {
    nearbyDogSuggestionSnapshotInputSchema,
    type NearbyDogSuggestionSnapshotInput,
} from "@wags/discovery/suggestions/nearby-dog-suggestion.contract.ts";
import {
    getDiscovery,
    getDiscoveryEventEmitter,
} from "@/lib/core/discovery-core.server";

async function serveNearbyDogSuggestionSnapshot({
    request,
    input,
    startedAt,
}: {
    request: Request;
    input: NearbyDogSuggestionSnapshotInput;
    startedAt: number;
}) {
    const discovery = await getDiscovery();
    const snapshot =
        await discovery.suggestions.getNearbyDogSuggestionSnapshot(input);
    const body = JSON.stringify(snapshot);
    const etag = `"${snapshot.revision}"`;
    const notModified = request.headers.get("if-none-match") === etag;
    const emitter = await getDiscoveryEventEmitter();
    emitter.emit("discovery.nearby_dog_suggestion.snapshot_served", {
        cell: input.cell,
        revision: snapshot.revision,
        rowCount: snapshot.rows.length,
        responseBytes: notModified
            ? 0
            : new TextEncoder().encode(body).byteLength,
        durationMs: Date.now() - startedAt,
        notModified,
    });
    const headers = {
        "cache-control":
            "public, max-age=300, s-maxage=600, stale-while-revalidate=600",
        "content-type": "application/json",
        etag,
    };
    if (notModified) {
        return new Response(null, { status: 304, headers });
    }
    return new Response(body, { headers });
}

export const nearbyDogSuggestionsApiRoute = {
    server: {
        handlers: {
            GET: createServerOnlyFn(
                async ({ request }: { request: Request }) => {
                    const startedAt = Date.now();
                    const url = new URL(request.url);
                    const input =
                        nearbyDogSuggestionSnapshotInputSchema.safeParse({
                            cell: url.searchParams.get("cell"),
                        });
                    if (!input.success) {
                        return Response.json(
                            { error: "Invalid location cell" },
                            { status: 400 },
                        );
                    }
                    try {
                        return await serveNearbyDogSuggestionSnapshot({
                            request,
                            input: input.data,
                            startedAt,
                        });
                    } catch (error) {
                        const emitter = await getDiscoveryEventEmitter().catch(
                            () => null,
                        );
                        if (emitter) {
                            emitter.log.error(
                                "nearby_dog_suggestions.snapshot_failed",
                                {
                                    cell: input.data.cell,
                                    error,
                                },
                            );
                            emitter.emit(
                                "discovery.nearby_dog_suggestion.snapshot_failed",
                                {
                                    cell: input.data.cell,
                                    durationMs: Date.now() - startedAt,
                                },
                            );
                            await emitter.flush();
                        }
                        return Response.json(
                            { error: "Nearby dog suggestions unavailable" },
                            { status: 500 },
                        );
                    }
                },
            ),
        },
    },
};
